import { useState, useEffect, useMemo } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Search, X, CalendarPlus, Scissors, PenLine, UserRound, Check } from 'lucide-react';
import { bookingApi } from '@/lib/bookingApi';
import { usersApi } from '@/lib/usersApi';
import { formatTime12, formatDateShortEs, formatDuration } from '@/utils/bookingFormat';
import { toast } from 'sonner';
import { tr } from '@/contexts/LanguageContext';
import { mergeServices } from '@/components/Booking/ServicesStep';
import { friendlyError } from '@/lib/apiError';
import { RecurringPicker, RecurringPreview } from '@/components/Profile/RecurringBooking';
import { ScheduleConflictDialog, isScheduleConflict, conflictRules } from '@/components/Profile/ScheduleConflictDialog';
import { DuplicateAskDialog, isNameDuplicateError } from '@/components/Clients/DuplicateAskDialog';

const toMin = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
const toHHMM = (v) => `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;

export const ManualBookingSheet = ({
  open, onOpenChange, barber, date, startMin, workStart, workEnd, onCreated,
}) => {
  const wsMin = workStart ? toMin(workStart) : 9 * 60;
  const weMin = workEnd ? toMin(workEnd) : 18 * 60;

  const [services, setServices] = useState([]);
  const [serviceIds, setServiceIds] = useState([]);  // LOTE 3: varios servicios (toca para añadir/quitar)
  const toggleService = (id) => setServiceIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : (ids.length >= 6 ? ids : [...ids, id])));
  const [mode, setMode] = useState('search'); // 'search' | 'manual'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [recognized, setRecognized] = useState(null);
  const [savedResults, setSavedResults] = useState([]);
  const [start, setStart] = useState(wsMin);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setServiceIds([]);
    setMode('search');
    setWalkIn(false); setNewClientAsk(false); setRecurring(null); setSeriesStep(false);
    setQuery('');
    setResults([]);
    setClient(null);
    setManualName('');
    setManualPhone('');
    setManualEmail('');
    setRecognized(null);
    setSavedResults([]);
    setNote('');
    setStart(startMin ?? wsMin);
    (async () => {
      try {
        const res = await bookingApi.services(barber.username);
        setServices((res?.items || []).filter((sv) => sv.active !== false));
      } catch {
        setServices([]);
      }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || mode !== 'search' || client || query.trim().length < 2) {
      setResults([]);
      setSavedResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { items } = await usersApi.searchMeta(query.trim(), { limit: 6 });
        setResults(items.filter((u) => u.id !== barber.id));
      } catch {
        setResults([]);
      }
      try {
        const { items: saved } = await bookingApi.clientsSearch(query.trim(), barber.id);
        setSavedResults(saved || []);
      } catch {
        setSavedResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, mode, client, open, barber.id]);

  // Autocompletado: si el teléfono ya existe en mi base de clientes,
  // rellena nombre/correo y muestra el chip de cliente reconocido.
  useEffect(() => {
    const digits = manualPhone.replace(/\D/g, '');
    if (!open || mode !== 'manual' || digits.length < 7) {
      setRecognized(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { client: found } = await bookingApi.clientLookup(digits, barber.id);
        if (found) {
          setRecognized(found);
          if (found.name) setManualName(found.name);
          if (found.email) setManualEmail(found.email);
        } else {
          setRecognized(null);
        }
      } catch {
        setRecognized(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [manualPhone, mode, open, barber.id]);

  const timeOptions = useMemo(() => {
    const out = [];
    for (let t = wsMin; t <= weMin; t += 15) out.push(t);
    // 1A — el inicio elegido en la agenda (pasos de 5 min) siempre aparece en la lista.
    if (start != null && start >= wsMin && start <= weMin && !out.includes(start)) out.push(start);
    return out.sort((a, b) => a - b);
  }, [wsMin, weMin, start]);

  const picked = serviceIds.map((id) => services.find((s) => s.id === id)).filter(Boolean);
  const service = picked.length ? mergeServices(picked) : null;
  // sep-2026 — el fin se DERIVA de la duración configurada del servicio (misma regla que el backend).
  const duration = service ? Number(service.duration_minutes) || 0 : 0;
  const end = service ? start + duration : null;
  const fitsDay = end !== null && end <= 24 * 60;
  // 21-oct-2026: cliente sin cuenta ⇒ nombre + (teléfono o correo); sin contacto solo cabe walk-in («Ahora no»).
  const clientReady = mode === 'search' ? Boolean(client) : (manualName.trim().length >= 2 && Boolean(manualPhone.trim() || manualEmail.trim()));
  const serviceReady = Boolean(service) && fitsDay && !saving;
  const canSave = clientReady && serviceReady;
  // A2: sin cliente → diálogo «¿Nuevo cliente?» (añadir datos o guardar directo como walk-in)
  const [newClientAsk, setNewClientAsk] = useState(false);
  const [walkIn, setWalkIn] = useState(false);
  const [recurring, setRecurring] = useState(null);     // A3: {every, count} | null
  const [seriesStep, setSeriesStep] = useState(false);  // A3: vista previa de la serie
  const seriesBase = () => ({
    barber_id: barber.id, service_id: service?.id, service_ids: serviceIds, date, start_time: toHHMM(start),
    client_user_id: !walkIn && mode === 'search' && client ? client.id : undefined,
    client_name: walkIn ? 'Walk-in' : (mode === 'manual' ? manualName.trim() : undefined),
    client_phone: !walkIn && mode === 'manual' ? (manualPhone.trim() || undefined) : undefined,
    client_email: !walkIn && mode === 'manual' ? (manualEmail.trim() || undefined) : undefined,
    note: note.trim() || undefined,
  });

  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictRulesList, setConflictRulesList] = useState([]);
  const [dupAsk, setDupAsk] = useState(null); // 21-oct-2026: aviso de nombre repetido {duplicates, allowOverlap}
  const save = async (allowOverlap = false, asWalkIn = walkIn, decision = {}) => {
    if (!asWalkIn && !canSave && !allowOverlap) return;
    if (!asWalkIn && mode === 'manual' && !manualPhone.trim() && !manualEmail.trim()) {
      toast.error(tr("Indica teléfono o correo del cliente (al menos uno)")); return;
    }
    setSaving(true);
    try {
      await bookingApi.createManual({
        allow_overlap: allowOverlap || undefined,
        barber_id: barber.id,
        service_id: service.id, service_ids: serviceIds,
        date,
        start_time: toHHMM(start),
        client_user_id: !asWalkIn && mode === 'search' && client ? client.id : undefined,
        client_name: asWalkIn ? 'Walk-in' : (mode === 'manual' ? manualName.trim() : undefined),
        client_phone: !asWalkIn && mode === 'manual' ? (manualPhone.trim() || undefined) : undefined,
        client_email: !asWalkIn && mode === 'manual' ? (manualEmail.trim() || undefined) : undefined,
        note: note.trim() || undefined,
        ...decision,
      });
      toast.success(tr("Cita creada · {0} · {1}", formatDateShortEs(date), formatTime12(toHHMM(start))));
      setConflictOpen(false); setDupAsk(null);
      onCreated();
    } catch (err) {
      if (isNameDuplicateError(err)) { setDupAsk({ duplicates: err.payload.detail.duplicates || [], allowOverlap }); return; }
      if (isScheduleConflict(err) && !allowOverlap) { setConflictRulesList(conflictRules(err)); setConflictOpen(true); return; }
      setConflictOpen(false);
      toast.error(friendlyError(err, tr("No pudimos crear la cita")));
    } finally {
      setSaving(false);
    }
  };

  return (<>
    <DuplicateAskDialog open={Boolean(dupAsk)} duplicates={dupAsk?.duplicates || []} busy={saving}
      onSame={(c) => save(dupAsk?.allowOverlap || false, false, { client_merge_into: c.id })}
      onDifferent={() => save(dupAsk?.allowOverlap || false, false, { client_force_new: true })}
      onCancel={() => setDupAsk(null)} />
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="bg-card border-border rounded-t-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-booking-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-lg text-foreground flex items-center gap-2">
            <CalendarPlus className="w-4 h-4 text-primary" />{' '}{tr("Cita nueva")}</SheetTitle>
          <p className="text-xs text-muted-foreground first-letter:uppercase" data-testid="manual-date-label">
            {formatDateShortEs(date)}
          </p>
        </SheetHeader>

        <div className="mt-3 space-y-4">
          {/* Cliente */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{tr("Cliente")}</p>
            <div className="flex items-center gap-1.5 mb-2">
              <button
                type="button"
                onClick={() => { setMode('search'); setManualName(''); setManualPhone(''); setManualEmail(''); setRecognized(null); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  mode === 'search' ? 'bg-primary/15 border-primary/60 text-primary' : 'bg-background border-border text-muted-foreground'
                }`}
                data-testid="manual-client-mode-search"
              >
                <Search className="w-3 h-3 inline mr-1 -mt-0.5" />{' '}{tr("Buscar cliente")}</button>
              <button
                type="button"
                onClick={() => { setMode('manual'); setClient(null); setQuery(''); setResults([]); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  mode === 'manual' ? 'bg-primary/15 border-primary/60 text-primary' : 'bg-background border-border text-muted-foreground'
                }`}
                data-testid="manual-client-mode-manual"
              >
                <PenLine className="w-3 h-3 inline mr-1 -mt-0.5" />{' '}{tr("Sin cuenta")}</button>
            </div>

            {mode === 'search' ? (
              client ? (
                <div className="flex items-center gap-3 bg-background border border-primary/40 rounded-xl p-2.5" data-testid="manual-client-selected">
                  <Avatar className="w-8 h-8 border border-primary/30">
                    <AvatarImage src={client.avatar} alt={client.name} />
                    <AvatarFallback>{(client.name || '?')[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{client.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">@{client.username}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setClient(null); setQuery(''); }}
                    className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0"
                    data-testid="manual-client-clear"
                    aria-label={tr("Quitar cliente")}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div>
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={tr("Nombre o @usuario…")}
                    className="h-10 rounded-xl bg-background border-border"
                    data-testid="manual-client-search-input"
                  />
                  {(results.length > 0 || savedResults.length > 0) && (
                    <div className="mt-1.5 bg-background border border-border rounded-xl overflow-hidden divide-y divide-border" data-testid="manual-client-results">
                      {results.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => { setClient(u); setResults([]); setSavedResults([]); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-primary/5 text-left transition-colors"
                          data-testid={`manual-client-result-${u.username}`}
                        >
                          <Avatar className="w-7 h-7">
                            <AvatarImage src={u.avatar} alt={u.name} />
                            <AvatarFallback>{(u.name || '?')[0]}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="text-sm text-foreground truncate">{u.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">@{u.username}</p>
                          </div>
                        </button>
                      ))}
                      {savedResults.filter((c) => !results.some((u) => u.id === c.user_id)).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            if (c.user_id) {
                              setClient({ id: c.user_id, name: c.name, username: c.username, avatar: c.avatar });
                            } else {
                              setMode('manual');
                              setManualName(c.name || '');
                              setManualPhone(c.phone_display || c.phone || '');
                              setManualEmail(c.email || '');
                            }
                            setResults([]); setSavedResults([]);
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-primary/5 text-left transition-colors"
                          data-testid={`manual-saved-client-${c.id}`}
                        >
                          <Avatar className="w-7 h-7">
                            <AvatarImage src={c.avatar} alt={c.name} />
                            <AvatarFallback><UserRound className="w-3.5 h-3.5" /></AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-foreground truncate">{c.name || c.phone_display || c.email}</p>
                            <p className="text-[11px] text-muted-foreground truncate">{tr("Cliente guardado{0} · {1} cita{2}", c.phone_display ? ` · ${c.phone_display}` : '', c.bookings_count, c.bookings_count === 1 ? '' : 's')}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="space-y-2">
                <Input
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder={tr("Nombre del cliente (walk-in)…")}
                  maxLength={80}
                  className="h-10 rounded-xl bg-background border-border"
                  data-testid="manual-client-name-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={manualPhone}
                    onChange={(e) => setManualPhone(e.target.value)}
                    placeholder={tr("Teléfono (o correo) *")}
                    maxLength={25}
                    inputMode="tel"
                    className="h-10 rounded-xl bg-background border-border"
                    data-testid="manual-client-phone-input"
                  />
                  <Input
                    value={manualEmail}
                    onChange={(e) => setManualEmail(e.target.value)}
                    placeholder={tr("Correo (o teléfono) *")}
                    maxLength={120}
                    inputMode="email"
                    className="h-10 rounded-xl bg-background border-border"
                    data-testid="manual-client-email-input"
                  />
                </div>
                {recognized ? (
                  <p className="text-[11px] text-primary flex items-center gap-1" data-testid="manual-client-recognized">
                    <UserRound className="w-3 h-3" />{recognized.bookings_count === 1 ? tr("Cliente reconocido · 1 cita previa — datos autocompletados") : tr("Cliente reconocido · {0} citas previas — datos autocompletados", recognized.bookings_count)}</p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">{tr("Si deja teléfono o correo, le enviaremos una invitación (única) para crear su cuenta.")}</p>
                )}
              </div>
            )}
          </div>

          {/* Servicio */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{tr("Servicio")}</p>
            {services.length === 0 ? (
              <p className="text-xs text-muted-foreground">{tr("No tienes servicios activos. Créalos en Citas y Agenda.")}</p>
            ) : (
              <div className="space-y-1.5">
                {services.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleService(s.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                      serviceIds.includes(s.id) ? 'bg-primary/10 border-primary/60' : 'bg-background border-border hover:border-primary/40'
                    }`}
                    data-testid={`manual-service-${s.id}`}
                  >
                    {serviceIds.includes(s.id) ? <Check className="w-3.5 h-3.5 shrink-0 text-primary" /> : <Scissors className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
                    <span className="flex-1 text-sm font-medium text-foreground truncate">{s.name}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0" data-testid={`manual-service-duration-${s.id}`}>{formatDuration(s.duration_minutes)}</span>
                    <span className="font-display text-sm font-bold text-primary shrink-0">${Number(s.price).toFixed(0)}</span>
                  </button>
                ))}
                {picked.length > 1 && (
                  <p className="text-[11px] text-primary tabular-nums px-1" data-testid="manual-services-total">
                    {tr("{0} servicios", picked.length)} · {service.name} · {formatDuration(service.duration_minutes)} · ${service.price.toFixed(2)}
                  </p>
                )}
                {picked.length <= 1 && <p className="text-[10px] text-muted-foreground px-1">{tr("Toca otro servicio para añadirlo a la misma cita.")}</p>}
              </div>
            )}
          </div>

          {/* Horario — el fin lo fija la duración del servicio */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{tr("Horario")}</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">{tr("Hora de inicio")}</p>
                <select
                  value={start}
                  onChange={(e) => setStart(Number(e.target.value))}
                  className="w-full h-10 rounded-xl bg-background border border-border text-sm text-foreground px-3"
                  data-testid="manual-start-select"
                >
                  {timeOptions.filter((t) => t < weMin).map((t) => (
                    <option key={t} value={t}>{formatTime12(toHHMM(t))}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">{tr("Hora de fin")}</p>
                <div
                  className={`w-full h-10 rounded-xl border text-sm px-3 flex items-center tabular-nums ${service ? 'bg-secondary/60 border-border text-foreground' : 'bg-background border-dashed border-border text-muted-foreground'}`}
                  aria-readonly="true"
                  data-testid="manual-end-readonly"
                >
                  {service ? formatTime12(toHHMM(end)) : '—'}
                </div>
              </div>
            </div>
            <p className={`text-[11px] mt-1.5 tabular-nums ${service && !fitsDay ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="manual-duration-hint">
              {!service
                ? tr("Elige un servicio: el fin se calcula con su duración")
                : !fitsDay
                  ? tr("Este servicio dura {0} min y no cabe en ese horario", duration)
                  : `Fin ${formatTime12(toHHMM(end))} · ${formatDuration(duration)} · ${service.name}`}
            </p>
          </div>

          {/* Barbero */}
          <div className="flex items-center gap-3 bg-background border border-border rounded-xl p-2.5" data-testid="manual-barber-row">
            <Avatar className="w-8 h-8 border border-primary/30">
              <AvatarImage src={barber.avatar} alt={barber.name} />
              <AvatarFallback><UserRound className="w-4 h-4" /></AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{barber.name || barber.username}</p>
              <p className="text-[11px] text-muted-foreground">{tr("Barbero")}</p>
            </div>
          </div>

          {/* Nota */}
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={tr("Nota interna (opcional)…")}
            maxLength={500}
            rows={2}
            className="rounded-xl bg-background border-border text-sm resize-none"
            data-testid="manual-note-input"
          />

          <RecurringPicker value={recurring} onChange={setRecurring} />
          {seriesStep && (
            <RecurringPreview base={seriesBase()} recurring={recurring} onBack={() => setSeriesStep(false)}
                              onDone={() => { setSeriesStep(false); setRecurring(null); onCreated(); }} />
          )}
          {newClientAsk && (
            <div className="rounded-xl border border-primary/40 bg-background p-3 space-y-2" data-testid="new-client-ask">
              <p className="text-sm font-semibold text-foreground">{tr("¿Nuevo cliente?")}</p>
              <p className="text-[11px] text-muted-foreground">{tr("Sin teléfono o correo no podremos enviarle recordatorios ni promociones.")}</p>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 rounded-full" onClick={() => { setNewClientAsk(false); setMode('manual'); }} data-testid="new-client-add-info">
                  {tr("Añadir información del cliente")}
                </Button>
                <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => { setNewClientAsk(false); setWalkIn(true); if (recurring) setSeriesStep(true); else save(false, true); }} data-testid="new-client-not-now">
                  {tr("Ahora no")}
                </Button>
              </div>
            </div>
          )}
          {!seriesStep && (
            <Button
              onClick={() => (clientReady ? (recurring ? setSeriesStep(true) : save(false)) : setNewClientAsk(true))}
              disabled={!serviceReady}
              className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 disabled:opacity-40"
              data-testid="manual-save-btn"
            >
              <CalendarPlus className="w-4 h-4" />
              {saving ? tr("Guardando…") : recurring ? tr("Ver fechas de la serie") : tr("Guardar cita")}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
    <ScheduleConflictDialog open={conflictOpen} busy={saving} rules={conflictRulesList} verb="agendar" onContinue={() => save(true)} onBack={() => setConflictOpen(false)} />
  </>);
};

export default ManualBookingSheet;
